import { FC, ReactNode, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"
import { PageWrapper } from "./Wrapper"
import { MAIN_COLORS } from "../../consts/colors"
import { appear, disappear, slidein, slideOut } from "../../consts/animations"
import BackIcon from '../../icons/back.svg'
import CloseIcon from '../../icons/close.svg'

interface IProps {
    fullscreen?: boolean
    position?: 'top' | 'center'
    close: () => void
    back?: () => void
    children: ReactNode
    isOpen: boolean
    closable?: boolean
}
const Overlay = styled.div<{ fullscreen: boolean, isClosing: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: ${p => p.fullscreen ? 'stretch' : 'flex-start'};
    z-index: 1000;
    animation: ${p => p.isClosing ? disappear : appear} 0.4s ease-out;
    padding: ${p => p.fullscreen ? '0' : '20px'};
`
const FullscreenModal = styled(PageWrapper)<{isClosing: boolean, $safeAreaTop: number }>`
    animation: ${p => p.isClosing ? slideOut : slidein} 0.4s ease-out;
    background: ${MAIN_COLORS.purple_100};
    margin: 0;
    padding-top: max(
      clamp(80px, 10dvh, 104px),
      ${p => p.$safeAreaTop + 12}px,
      calc(env(safe-area-inset-top) + 12px),
      calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px) + 12px)
    );
    padding-bottom: max(24px, env(safe-area-inset-bottom));
    width: 100%;
    overflow: auto;
`
const TopModal = styled.div<{isClosing: boolean, $safeAreaTop: number }>`
    width: 90%;
    max-width: 500px;
    margin-top: max(
      ${p => p.$safeAreaTop + 20}px,
      calc(env(safe-area-inset-top) + 20px),
      calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px) + 20px)
    );
    animation: ${p => p.isClosing ? slideOut : slidein} 0.4s ease-out;
    background: ${MAIN_COLORS.gradient_purple_200};
    padding: 16px;
    position: relative;
    border-radius: 14px;
    pointer-events: auto;

    &::before {
      pointer-events: none;
      content: "";
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: inherit;
      background: ${MAIN_COLORS.gradient_yellow_pink};
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      transition: opacity 0.2s;
    }
`
const CenterModal = styled.div<{isClosing: boolean }>`
    width: 90%;
    max-width: 400px;
    margin-top: 50%;
    //margin-top: auto;
    //margin-bottom: auto;
    animation: ${p => p.isClosing ? slideOut : slidein} 0.4s ease-out;
    background: ${MAIN_COLORS.gradient_purple_200};
    padding: 16px;
    position: relative;
    border-radius: 14px;
    pointer-events: auto;

    &::before {
      pointer-events: none;
      content: "";
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: inherit;
      background: ${MAIN_COLORS.gradient_yellow_pink};
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      transition: opacity 0.2s;
    }
`

const Modal:FC<IProps> = ({ 
        fullscreen = false, 
        position = 'center', 
        close, 
        back,
        children, 
        isOpen,
        closable = true
    }) => {
        const [ isClosing, setIsClosing ] = useState(false);
        const [ shouldRender, setShouldRender ] = useState(isOpen);
        const webApp = window.Telegram?.WebApp;
        const safeAreaTop = Number(webApp?.safeAreaInset?.top || 0)
          + Number(webApp?.contentSafeAreaInset?.top || 0);

        useEffect(() => {
          if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
          } else {
            handleClose();
          }
        }, [isOpen])

        const handleClose = () => {
          setIsClosing(true);
          setTimeout(() => {
            setShouldRender(false);
            close();
          }, 400)
        }

        return !shouldRender ? null : createPortal(
            <Overlay isClosing={isClosing} fullscreen={fullscreen}>
              {fullscreen ? (
                <FullscreenModal isClosing={isClosing} $safeAreaTop={safeAreaTop}>
                  {closable && <Header onBack={back} onClose={handleClose}/>}
                  {children}
                </FullscreenModal>
              ) : position === 'top' ? (
                <TopModal isClosing={isClosing} $safeAreaTop={safeAreaTop}>
                  <Header onClose={handleClose}/>
                  {children}
                </TopModal>
              ) : (
                <CenterModal isClosing={isClosing}>
                  <Header onClose={handleClose}/>
                  {children}
                </CenterModal>
              )}
            </Overlay>,
            document.body
        )
}

export default Modal

const HeaderWrap = styled.div<{saveArea:any}>`
    display: flex;
    justify-content: space-between;

    img {
      height: 24px;
      width: 24px;  
    }
`
const Header = ({ onBack, onClose }: { onBack?: ()=> void, onClose: () => void }) => {
    const items = [
        {src: onBack ? BackIcon : 'flex', click:onBack },
        {src: CloseIcon, click: onClose }
    ];

    return (
        <HeaderWrap saveArea={onBack ? window.Telegram.WebApp.safeAreaInset.top + window.Telegram.WebApp.contentSafeAreaInset.top : null}>
            {Array.from(items).map((item) => item.src === 'flex' 
                ? (<div style={{flex:1}}/>) : 
                (
                <img
                    src={item.src}
                    onClick={item.click}
                    alt=""
                />
                )
            )}
        </HeaderWrap>
    )
}
